import { useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { HubResponse, PeriodMetrics } from "@/lib/consultant-hub";
import { cn } from "@/lib/utils";
import {
  FySelect,
  fmtInt,
  fmtK,
  fmtMoney,
  fmtMoneyShort,
  fmtPct,
  GRAIN_OPTIONS,
  KBreakdown,
  KByTerritory,
  ProductSelect,
  ScopeScoreboard,
  type ScoreboardRow,
  Toggle,
  useHubData,
  useHubFilters,
  YoyBadge,
} from "./shared";

function StatTile({
  label,
  value,
  sub,
  children,
}: {
  label: string;
  value: string;
  sub?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
        {children}
        {sub && <span>{sub}</span>}
      </div>
    </div>
  );
}

const th = "px-3 py-2 text-right font-medium text-muted-foreground whitespace-nowrap";
const td = "px-3 py-2 text-right tabular-nums whitespace-nowrap";

export function ConsultantSummaryPage() {
  const { grain, setGrain, fy, setFy, product, setProduct } = useHubFilters();
  const [periodKey, setPeriodKey] = useState<string | null>(null);
  const [scopeKey, setScopeKey] = useState("all");
  const { data, error, loading } = useHubData(grain, fy, product);

  // Default to the in-progress period (or the latest one with data) whenever the columns change.
  useEffect(() => {
    if (!data) return;
    if (periodKey && data.periods.some((p) => p.key === periodKey)) return;
    const elapsed = data.periods.filter((p) => p.status !== "future");
    setPeriodKey(data.meta.currentPeriodKey ?? elapsed[elapsed.length - 1]?.key ?? null);
  }, [data, periodKey]);

  const idx = data ? data.periods.findIndex((p) => p.key === periodKey) : -1;
  const period = data && idx >= 0 ? data.periods[idx] : undefined;
  // Headline tiles + lead sources follow the territory picker (default: whole company).
  const scope = data?.scopes.find((s) => s.key === scopeKey);
  const all = data && idx >= 0 ? (data.data[scope?.key ?? "all"]?.[idx] ?? null) : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Consultants — Executive Summary"
        description="Opportunities, closed deals, sales and referral rate (K) for the whole consultant team, by territory."
      />

      <div className="flex flex-wrap items-center gap-3">
        <Toggle
          value={grain}
          options={GRAIN_OPTIONS}
          onChange={(g) => {
            setGrain(g);
            setPeriodKey(null);
          }}
        />
        {grain !== "year" && (
          <FySelect
            data={data}
            fy={fy}
            onChange={(v) => {
              setFy(v);
              setPeriodKey(null);
            }}
          />
        )}
        {data && (
          <select
            className="h-8 rounded-md border bg-background px-2 text-sm"
            value={periodKey ?? ""}
            onChange={(e) => setPeriodKey(e.target.value)}
          >
            {data.periods
              .filter((p) => p.status !== "future")
              .map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                  {p.status === "current" ? " (to date)" : ""}
                </option>
              ))}
          </select>
        )}
        <ProductSelect data={data} value={product} onChange={setProduct} />
        {data && (
          <select
            className="h-8 rounded-md border bg-background px-2 text-sm"
            value={scopeKey}
            onChange={(e) => setScopeKey(e.target.value)}
          >
            {data.scopes.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
                {s.consultant
                  ? ` — ${s.consultant}`
                  : s.kind === "division"
                    ? ` — ${s.divisionHead}`
                    : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {error}
          </CardContent>
        </Card>
      )}
      {loading && !data && <Skeleton className="h-64 w-full" />}

      {data && all && period && (
        <div className={cn("space-y-6", loading && "opacity-60")}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <StatTile label="Fiscal YTD K" value={fmtK(all.k)} sub={`Rolling K ${fmtK(all.k12)}`} />
            <StatTile label="Opportunities" value={fmtInt(all.opps)}>
              <YoyBadge cur={all.opps} prior={all.prior.opps} />
            </StatTile>
            <StatTile label="Closed deals" value={fmtInt(all.closed)}>
              <YoyBadge cur={all.closed} prior={all.prior.closed} />
            </StatTile>
            <StatTile label="Close rate" value={fmtPct(all.closeRate)} />
            <StatTile label="Gross sales" value={fmtMoneyShort(all.gross)}>
              <YoyBadge cur={all.gross} prior={all.prior.gross} />
            </StatTile>
            <StatTile label="Net sales" value={fmtMoneyShort(all.net)}>
              <YoyBadge cur={all.net} prior={all.prior.net} />
            </StatTile>
          </div>
          {period.status === "current" && (
            <p className="-mt-3 text-xs text-muted-foreground">
              {period.label} is in progress — compared to the same dates last year.
            </p>
          )}

          <KBreakdown
            m={all}
            asOfLabel={`${scope && scope.key !== "all" ? `${scope.label} · ` : ""}${grain === "year" ? period.label : `${data.fyLabel} through ${period.label}`}${period.status === "current" ? " (to date)" : ""}`}
            note="K by territory below shows the same math for every division and territory."
          />

          <TerritoryScoreboard data={data} idx={idx} />

          <KByTerritory
            rows={scoreboardRows(data)}
            metricsFor={(key) => data.data[key]?.[idx] ?? null}
            total={data.data.all?.[idx] ?? null}
            totalLabel="Company total"
            asOfLabel={`${grain === "year" ? period.label : `${data.fyLabel} through ${period.label}`}`}
          />

          <SourcesCard
            data={data}
            m={all}
            title={scope && scope.key !== "all" ? scope.label : null}
          />

          <SourcesByTerritoryCard data={data} idx={idx} />

          <p className="text-xs text-muted-foreground">
            Fiscal YTD K = referral closes (Friend + Affiliate) since April 1 ÷ all closes over the
            same dates last year. Rolling K = the last 12 months ÷ the 12 months before. Territories
            are assigned by the customer's state; {data.meta.locatedPct}% of closed deals in this
            view have a state on file.
          </p>
        </div>
      )}
    </div>
  );
}

/** Divisions first, each followed by its territories; then deals with no state. */
function scoreboardRows(data: HubResponse): ScoreboardRow[] {
  const rows: ScoreboardRow[] = [];
  for (const d of data.scopes.filter((s) => s.kind === "division")) {
    rows.push({ scope: d, level: "division" });
    for (const key of d.territories ?? []) {
      const t = data.scopes.find((s) => s.key === key);
      if (t) rows.push({ scope: t, level: "territory" });
    }
  }
  for (const u of data.scopes.filter((s) => s.kind === "unassigned"))
    rows.push({ scope: u, level: "other" });
  return rows;
}

function TerritoryScoreboard({ data, idx }: { data: HubResponse; idx: number }) {
  const rows = scoreboardRows(data);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Division &amp; territory scoreboard</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <ScopeScoreboard
          rows={rows}
          metricsFor={(key) => data.data[key]?.[idx] ?? null}
          total={data.data.all?.[idx] ?? null}
          totalLabel="Company total"
        />
      </CardContent>
    </Card>
  );
}

function SourcesCard({
  data,
  m,
  title,
}: {
  data: HubResponse;
  m: PeriodMetrics;
  title: string | null;
}) {
  const rows = data.buckets
    .map((b) => {
      const opps = m.oppsBySource[b.key] ?? 0;
      const closed = m.closedBySource[b.key] ?? { count: 0, gross: 0 };
      return { ...b, opps, closed: closed.count, gross: closed.gross };
    })
    .filter((r) => r.opps || r.closed)
    .sort((a, b) => b.opps - a.opps);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Where opportunities and closed deals come from{title ? ` — ${title}` : ""}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Source</th>
              <th className={th}>Opps</th>
              <th className={th}>% opps</th>
              <th className={th}>Closed</th>
              <th className={th}>% closed</th>
              <th className={th}>Close rate</th>
              <th className={th}>Gross</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b last:border-0">
                <td className="px-3 py-2 font-medium">{r.label}</td>
                <td className={td}>{fmtInt(r.opps)}</td>
                <td className={td}>{fmtPct(m.opps ? r.opps / m.opps : null)}</td>
                <td className={td}>{fmtInt(r.closed)}</td>
                <td className={td}>{fmtPct(m.closed ? r.closed / m.closed : null)}</td>
                <td className={td}>{fmtPct(r.opps ? r.closed / r.opps : null)}</td>
                <td className={td}>{fmtMoney(r.gross)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

type SourceMetric = "opps" | "closed" | "closeRate" | "gross";
const SOURCE_METRIC_OPTIONS: { value: SourceMetric; label: string }[] = [
  { value: "opps", label: "Opportunities" },
  { value: "closed", label: "Closed deals" },
  { value: "closeRate", label: "Close rate" },
  { value: "gross", label: "Gross sales" },
];

/** Lead sources (rows) × territories (columns) for the selected period. */
function SourcesByTerritoryCard({ data, idx }: { data: HubResponse; idx: number }) {
  const [metric, setMetric] = useState<SourceMetric>("opps");
  const cols = data.scopes.filter((s) => s.kind === "territory" || s.kind === "unassigned");
  const colsWithTotal = [...cols, { key: "all", label: "Company total" }];
  const metricsFor = (key: string) => data.data[key]?.[idx] ?? null;

  const raw = (m: PeriodMetrics | null, bucket: string | null) => {
    if (!m) return { opps: 0, closed: 0, gross: 0 };
    if (bucket === null) return { opps: m.opps, closed: m.closed, gross: m.gross };
    const c = m.closedBySource[bucket];
    return { opps: m.oppsBySource[bucket] ?? 0, closed: c?.count ?? 0, gross: c?.gross ?? 0 };
  };
  const fmt = (v: { opps: number; closed: number; gross: number }) =>
    metric === "opps"
      ? fmtInt(v.opps)
      : metric === "closed"
        ? fmtInt(v.closed)
        : metric === "gross"
          ? fmtMoney(v.gross)
          : fmtPct(v.opps ? v.closed / v.opps : null);

  const all = metricsFor("all");
  const rows = data.buckets.filter((b) => {
    const v = raw(all, b.key);
    return v.opps || v.closed;
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="text-base">Lead sources by territory</CardTitle>
        <Toggle value={metric} options={SOURCE_METRIC_OPTIONS} onChange={setMetric} />
      </CardHeader>
      <CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40">
            <tr>
              <th className="sticky left-0 bg-muted px-3 py-2 text-left font-medium text-muted-foreground">
                Source
              </th>
              {colsWithTotal.map((c) => (
                <th
                  key={c.key}
                  className={cn(
                    th,
                    "whitespace-normal align-bottom",
                    c.key === "all" && "border-l text-foreground",
                  )}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.key} className="border-b last:border-0">
                <td className="sticky left-0 bg-background px-3 py-2 font-medium whitespace-nowrap">
                  {b.label}
                </td>
                {colsWithTotal.map((c) => (
                  <td
                    key={c.key}
                    className={cn(td, c.key === "all" && "border-l bg-muted/20 font-semibold")}
                  >
                    {fmt(raw(metricsFor(c.key), b.key))}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t-2 bg-muted/30 font-semibold">
              <td className="sticky left-0 bg-muted px-3 py-2">Total</td>
              {colsWithTotal.map((c) => (
                <td key={c.key} className={cn(td, c.key === "all" && "border-l")}>
                  {fmt(raw(metricsFor(c.key), null))}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
